import {
  managementInputStyle,
  managementModalStyle,
  StyledModal,
} from "pages/PatientOverview/styles";
import { enqueueSnackbar } from "notistack";
import { alphabeticalOnlyRegex } from "constants/regex";
import { capitalize } from "utils/formatters";
import { Box, Grid, TextField } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { CircularProgress } from "@material-ui/core";
import { icons } from "constants/icons";
import { Switch } from "components/Switch/Switch";
import {
  CORE_MANAGEMENT_GROUPS,
  MANAGEMENT_GROUPS,
  USER_GROUPS,
  USER_GROUPS_TOOLTIP,
  userOperations,
} from "constants/roles";
import { Button } from "components/Buttons/Button";
import { ButtonColorScheme, ButtonType } from "types/ui";
import { useAppSelector, useAppDispatch } from "hooks/reduxHooks";
import {
  selectSelectedClinician,
  selectCurrentClinicianLoading,
  selectSelectedClinicianRoles,
  selectCurrentClinician,
} from "store/ClinicianStore/selectors";
import { formatName } from "../common/functions";
import { useContext, useEffect, useState } from "react";
import {
  deleteClinician,
  editClinicianCognitoGroups,
  getCliniciansList,
  getCurrentClinician,
  updateClinician,
} from "store/ClinicianStore/actions";
import {
  CreateClinicianPayload,
  UpdateClinicianPayload,
} from "repositories/ClinicRepository";
import { useValidEmail } from "hooks/useValidEmail";
import {
  setOffset,
  setSelectedClinician,
  setTotal,
} from "store/ClinicianStore/clinicianSlice";
import { AuthContext } from "contexts/authContext";
import { ClinicRepository } from "../../../../../repositories/ClinicRepository";
import { styled } from "@mui/material/styles";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

type Props = {
  isEnabled: boolean;
  setIsEnabled: any;
  operation: userOperations;
  setOpen: any;
  setError: any;
};

export const CreateEditModal = ({
  isEnabled,
  setIsEnabled,
  operation,
  setOpen,
  setError,
}: Props) => {
  const authContext = useContext(AuthContext);
  const selectedClinician = useAppSelector(selectSelectedClinician);
  const selectedClinicianRoles = useAppSelector(selectSelectedClinicianRoles);
  const loading = useAppSelector(selectCurrentClinicianLoading);
  const dispatch = useAppDispatch();
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [newClinicianName, setNewClinicianName] = useState({
    firstName: "",
    lastName: "",
  });
  const currentClinician = useAppSelector(selectCurrentClinician);
  const [inviting, setInviting] = useState(false);
  const {
    email: newClinicianEmail,
    setEmail: setNewClinicianEmail,
    emailIsValid: newClinicianEmailIsValid,
  } = useValidEmail("");

  const addToSelectedRoles = (value: string) => {
    const newRoles = [...selectedRoles, value];
    const targetRoles = [
      USER_GROUPS.CLINIC_TEAM_LEAD,
      USER_GROUPS.CLINIC_TEAM_MEMBER,
    ];
    const hasTargetRoles = newRoles.some((role) => targetRoles.includes(role));
    const dontHaveCliniciansRole = !selectedRoles.includes(
      USER_GROUPS.CLINICIAN,
    );

    if (hasTargetRoles && dontHaveCliniciansRole) {
      setSelectedRoles([...newRoles, USER_GROUPS.CLINICIAN]);
    } else {
      setSelectedRoles([...selectedRoles, value]);
    }
  };

  const removeFromSelectedRoles = (value: string) => {
    const targetRoles = [
      USER_GROUPS.CLINIC_TEAM_LEAD,
      USER_GROUPS.CLINIC_TEAM_MEMBER,
    ];
    const hasTargetRoles = selectedRoles.some((role) =>
      targetRoles.includes(role),
    );

    if (hasTargetRoles && value === USER_GROUPS.CLINICIAN) {
      return;
    }
    setSelectedRoles(selectedRoles.filter((item) => item !== value));
  };

  const handleChange = (value: string) => {
    if (selectedRoles.includes(value)) {
      removeFromSelectedRoles(value);
    } else {
      addToSelectedRoles(value);
    }
  };

  useEffect(() => {
    setSelectedRoles(
      operation == userOperations.EDIT ? selectedClinicianRoles ?? [] : [],
    );
  }, [selectedClinicianRoles]);

  const updateUI = () => {
    dispatch(setOffset(0));
    dispatch(setTotal(0));
    dispatch(getCurrentClinician());
    dispatch(getCliniciansList());
    clearData();
  };

  const clearData = () => {
    setSelectedRoles([]);
    dispatch(setSelectedClinician(undefined));
    setIsEnabled(false);
  };

  const updateSelectedClinician = async () => {
    if (selectedClinician?._id) {
      const payload: UpdateClinicianPayload = {
        role: selectedRoles,
      };
      if (!payload.role) {
        throw new Error("No roles selected");
      }
      // DB ROLE UPDATE
      dispatch(updateClinician(selectedClinician?._id, payload));
      const selectedUserEmail = selectedClinician?.profileInfo?.email;
      if (selectedUserEmail && authContext.adminListGroupsForUser) {
        const userGroupList = await authContext.adminListGroupsForUser(
          selectedUserEmail,
        );
        const currentGroupsRaw = userGroupList.data.Groups || [];
        const currentGroups = currentGroupsRaw?.map(
          (item: { GroupName: string }) => item.GroupName,
        );

        const rolesToDelete = currentGroups.filter(
          (currentGroup: string) => !payload.role?.includes(currentGroup),
        );

        const rolesToAdd = payload.role.filter(
          (role) => !currentGroups.includes(role),
        );
        // COGNITO ROLE UPDATE
        dispatch(
          editClinicianCognitoGroups(
            selectedClinician?._id,
            rolesToDelete,
            rolesToAdd,
            selectedUserEmail,
            authContext.adminAddUserToGroup,
          ),
        );
      } else {
        throw new Error("Invlid roles");
      }

      updateUI();
    }
  };

  const handleReInvite = async () => {
    setInviting(true);
    if (selectedClinician) {
      try {
        await authContext.reSendClinicInvite(
          selectedClinician.profileInfo?.email,
          selectedClinician.role,
          true,
        );
        const clinicianRepo = ClinicRepository.getInstance();
        const inviteDates = selectedClinician.profileInfo.inviteDates || [];
        const newInviteDates = [
          ...inviteDates,
          new Date().toISOString().slice(0, 10),
        ];
        await clinicianRepo.updateCurrentClinician(selectedClinician._id, {
          inviteDates: newInviteDates,
          authStatus: "invited",
        });
        setError(false);
      } catch (e) {
        setInviting(false);
        setError(true);
      }
    }
    updateUI();
    setOpen(true);
    setInviting(false);
  };
  const deleteSelectedClinician = async () => {
    if (selectedClinician?._id) {
      dispatch(deleteClinician(selectedClinician?._id, updateUI));
    }
  };

  const inviteClinician = async () => {
    if (!newClinicianEmail) {
      return enqueueSnackbar("Please enter a valid email", {
        variant: "error",
      });
    }

    if (!currentClinician?._id || selectedRoles.length < 1) {
      return enqueueSnackbar("Please enter at least one role", {
        variant: "error",
      });
    }

    if (!newClinicianName.firstName || !newClinicianName.lastName) {
      return enqueueSnackbar("Please enter a valid name and last name", {
        variant: "error",
      });
    }

    setInviting(true);
    try {
      const payload: CreateClinicianPayload = {
        firstName: newClinicianName.firstName,
        lastName: newClinicianName.lastName,
        email: newClinicianEmail.toLowerCase(),
        clinicId: currentClinician?.clinicId ?? "",
        role: selectedRoles,
      };
      const clinicianRepository = ClinicRepository.getInstance();
      await clinicianRepository.createClinicianOnInvite(payload);

      //Create user in cognito
      await authContext.signUpAdmin(newClinicianEmail.toLowerCase());
      //assign roles
      if (selectedRoles && selectedRoles.length > 0) {
        selectedRoles.forEach((e) => {
          if (authContext.adminAddUserToGroup) {
            authContext?.adminAddUserToGroup(
              newClinicianEmail.toLowerCase(),
              e,
            );
          }
        });
      } else {
        throw new Error("Invliad roles");
      }

      setError(false);
    } catch (e) {
      setError(true);
    }
    updateUI();
    setOpen(true);
    setInviting(false);
  };

  if (operation == userOperations.INVITE) {
    return (
      <StyledModal
        aria-labelledby="unstyled-modal-title"
        aria-describedby="unstyled-modal-description"
        open={isEnabled}
      >
        <Box sx={{ ...managementModalStyle, height: "fit-content" }}>
          {loading ? (
            <CircularProgress color="inherit" size={20} />
          ) : (
            <>
              <Grid container mt={1} mb={1}>
                <Grid item xs={11} sx={{ fontWeight: "700", fontSize: 16 }}>
                  Resend user invitation
                </Grid>
                <Grid item xs={1}>
                  <img
                    src={icons.modalClose}
                    alt="close"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      updateUI();
                    }}
                    aria-hidden="true"
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} mt={1} mb={1}>
                Are you sure you want to resend invitation for this user?
              </Grid>
              <Grid container mt={1} mb={1}>
                <Grid xs={12} display="flex" justifyContent="flex-end" mt={3}>
                  {inviting ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : (
                    <Button
                      title="Confirm"
                      onClick={() => handleReInvite()}
                      type={ButtonType.normal}
                      colorScheme={ButtonColorScheme.accent}
                      style={{
                        fontSize: "18px",
                        fontWeight: "400",
                        backgroundColor: "#05B2DC",
                      }}
                    />
                  )}
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      </StyledModal>
    );
  }

  const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(() => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "#ffffff",
      color: "rgba(0, 0, 0, 0.87)",
      padding: 15,
      borderRadius: 10,
      border: "1px solid #dadde9",
    },
  }));

  const getTooltipText = (title: string) => {
    switch (title) {
      case USER_GROUPS.CLINIC_ADMIN:
        return USER_GROUPS_TOOLTIP.CLINIC_ADMIN;
      case USER_GROUPS.CLINIC_ASSISTANT:
        return USER_GROUPS_TOOLTIP.CLINIC_ASSISTANT;
      case USER_GROUPS.CLINIC_TEAM_LEAD:
        return USER_GROUPS_TOOLTIP.CLINIC_TEAM_LEAD;
      case USER_GROUPS.CLINIC_TEAM_MEMBER:
        return USER_GROUPS_TOOLTIP.CLINIC_TEAM_MEMBER;
      case USER_GROUPS.CLINICIAN:
        return USER_GROUPS_TOOLTIP.CLINICIAN;
    }
  };

  const roleLine = (e: string) => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <p
            style={{
              marginLeft: 0,
              justifyContent: "space-between",
              color: "#5C5D69",
              fontSize: "14px",
              fontWeight: "400",
              marginTop: 4,
              marginRight: 7,
              marginBottom: 4,
            }}
          >
            {formatName(e)}
          </p>
          <HtmlTooltip
            title={
              <>
                <Typography
                  color="inherit"
                  style={{ fontSize: "22px", fontWeight: "800" }}
                >
                  {formatName(e)}
                </Typography>
                <p
                  style={{
                    fontSize: "16px",
                    marginTop: "10px",
                  }}
                >
                  {getTooltipText(e)}
                </p>
              </>
            }
          >
            <InfoOutlinedIcon style={{ width: "15px" }} color="primary" />
          </HtmlTooltip>
        </div>
        <Switch
          active={selectedRoles?.includes(e) ?? false}
          onChange={() => handleChange(e)}
        />
      </div>
    );
  };

  return (
    <StyledModal
      aria-labelledby="unstyled-modal-title"
      aria-describedby="unstyled-modal-description"
      open={isEnabled}
    >
      <Box sx={{ ...managementModalStyle, height: "fit-content" }}>
        {loading ? (
          <CircularProgress color="inherit" size={20} />
        ) : operation == userOperations.EDIT ||
          operation == userOperations.CREATE ? (
          <>
            <Grid container mt={1} mb={1}>
              <Grid item xs={11} sx={{ fontWeight: "700", fontSize: 16 }}>
                {operation == userOperations.EDIT
                  ? selectedClinician?.profileInfo?.firstName + " Permissions"
                  : "Create New User"}
              </Grid>
              <Grid item xs={1}>
                <img
                  src={icons.modalClose}
                  alt="close"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    clearData();
                  }}
                  aria-hidden="true"
                />
              </Grid>
            </Grid>
            {operation != userOperations.EDIT && (
              <Grid container mt={1} mb={1}>
                <Grid item xs={12}>
                  <TextField
                    size="small"
                    variant="standard"
                    placeholder="Enter Name"
                    value={newClinicianName?.firstName}
                    fullWidth
                    required
                    style={managementInputStyle}
                    InputProps={{
                      disableUnderline: true,
                      style: { color: "#5C5D68" },
                    }}
                    onChange={(e: any) => {
                      if (alphabeticalOnlyRegex.test(e.target.value)) {
                        setNewClinicianName({
                          ...newClinicianName,
                          firstName: capitalize(e.target.value),
                        });
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    size="small"
                    variant="standard"
                    placeholder="Enter Last Name"
                    value={newClinicianName?.lastName}
                    fullWidth
                    required
                    style={managementInputStyle}
                    InputProps={{
                      disableUnderline: true,
                      style: { color: "#5C5D68" },
                    }}
                    onChange={(e: any) => {
                      if (alphabeticalOnlyRegex.test(e.target.value)) {
                        setNewClinicianName({
                          ...newClinicianName,
                          lastName: capitalize(e.target.value),
                        });
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    size="small"
                    variant="standard"
                    placeholder="Email"
                    value={newClinicianEmail}
                    fullWidth
                    required
                    style={managementInputStyle}
                    InputProps={{
                      disableUnderline: true,
                      style: { color: "#5C5D68" },
                    }}
                    onChange={(e: any) => {
                      setNewClinicianEmail(e.target.value);
                    }}
                  />
                </Grid>
              </Grid>
            )}
            <Grid container mt={1} mb={1}>
              {operation != userOperations.EDIT && (
                <Grid item xs={12} sx={{ fontWeight: "700", fontSize: 16 }}>
                  {"Select a Role"}
                </Grid>
              )}
              <Grid
                item
                xs={12}
                sx={{ fontWeight: "400", fontSize: 12 }}
                mt={1}
              >
                Admin Permissions
              </Grid>
              <Grid item xs={12}>
                {MANAGEMENT_GROUPS.map((e: string) => roleLine(e))}
              </Grid>
              <Grid
                item
                xs={12}
                sx={{ fontWeight: "400", fontSize: 12 }}
                mt={1.2}
              >
                Care Permissions
              </Grid>
              <Grid item xs={12}>
                {CORE_MANAGEMENT_GROUPS.map((e) => roleLine(e))}
              </Grid>
              <Grid xs={12} display="flex" justifyContent="flex-end" mt={3}>
                {operation != userOperations.EDIT ? (
                  <Button
                    onClick={inviteClinician}
                    disabled={
                      newClinicianEmailIsValid != "" ||
                      selectedRoles.length < 1 ||
                      inviting
                    }
                    type={ButtonType.normal}
                    colorScheme={
                      newClinicianEmailIsValid != "" ||
                      selectedRoles.length < 1 ||
                      inviting
                        ? ButtonColorScheme.disabled
                        : ButtonColorScheme.gradient
                    }
                    style={{
                      marginRight: -10,
                      fontSize: "18px",
                      fontWeight: "400",
                    }}
                  >
                    {inviting ? <CircularProgress size={20} /> : "Invite"}
                  </Button>
                ) : (
                  <Button
                    title="Confirm"
                    onClick={updateSelectedClinician}
                    type={ButtonType.normal}
                    colorScheme={ButtonColorScheme.accent}
                    style={{
                      marginRight: -10,
                      fontSize: "18px",
                      fontWeight: "400",
                      backgroundColor: "#05B2DC",
                    }}
                  />
                )}
              </Grid>
            </Grid>
          </>
        ) : (
          <>
            <Grid container mt={1} mb={1}>
              <Grid item xs={11} sx={{ fontWeight: "700", fontSize: 16 }}>
                Delete User
              </Grid>
              <Grid item xs={1}>
                <img
                  src={icons.modalClose}
                  alt="close"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    updateUI();
                  }}
                  aria-hidden="true"
                />
              </Grid>
            </Grid>
            <Grid item xs={12} mt={1} mb={1}>
              Are you sure you want to disable this user credentials?
            </Grid>
            <Grid container mt={1} mb={1}>
              <Grid xs={12} display="flex" justifyContent="flex-end" mt={3}>
                <Button
                  title="Confirm"
                  onClick={deleteSelectedClinician}
                  type={ButtonType.normal}
                  colorScheme={ButtonColorScheme.accent}
                  style={{
                    fontSize: "18px",
                    fontWeight: "400",
                    backgroundColor: "#05B2DC",
                  }}
                />
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </StyledModal>
  );
};
